import { useState } from "react";
import { Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import FixedSave from "../../../../components/Actions/FixedSave";
import DefaultButton from "../../../../components/Buttons/Default";
import IconButton from "../../../../components/Buttons/IconButton";
import { BsTrash3 } from "react-icons/bs";
import { post } from "../../../../utils/helpers/api";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import WarehouseSelect from "../../../../components/Forms/WarehouseSelect";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";

const EMPTY_LINE = { product_id: "", product_name: "", quantity_requested: "" };

function CreateTransferPage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const navigate = useNavigate();

  const [saveLoading, setSaveLoading] = useState(false);
  const [form, setForm] = useState({
    source_warehouse_id: "",
    destination_warehouse_id: "",
    transfer_date: new Date(),
    driver_name: "",
    vehicle_registration: "",
    notes: "",
  });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateLine = (index, patch) =>
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const submit = async () => {
    if (!form.source_warehouse_id) return toast.error(t('transfers.sourceRequired'));
    if (!form.destination_warehouse_id) return toast.error(t('transfers.destinationRequired'));
    if (form.source_warehouse_id === form.destination_warehouse_id) {
      return toast.error(t('transfers.sameWarehouse'));
    }

    const payloadLines = lines
      .filter((line) => line.product_id && Number(line.quantity_requested) > 0)
      .map((line) => ({
        product_id: line.product_id,
        quantity_requested: Number(line.quantity_requested),
      }));

    if (!payloadLines.length) return toast.error(t('transfers.linesRequired'));

    setSaveLoading(true);

    const data = await post('/inventory/stock-transfers/store', {
      ...form,
      transfer_date: format(form.transfer_date, 'yyyy-MM-dd'),
      lines: payloadLines,
    });

    if (data?.success) {
      toast.success(t('transfers.created'));
      navigate('/inventory/stock-transfers');
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }

    setSaveLoading(false);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('transfers.title'), t('transfers.newTransfer')]}
          onBack={() => navigate('/inventory/stock-transfers')}
        />

        <div className="subheader-page-content scrollable">
          <Row className="g-3 mb-3">
            <Col md="4">
              <Label>{t('transfers.from')}: <span className="text-danger">*</span></Label>
              {/* Both ends list every warehouse the user can reach, since a
                  transfer legitimately crosses branches. */}
              <WarehouseSelect
                allBranches
                value={form.source_warehouse_id}
                onChange={(v) => update('source_warehouse_id', v)}
              />
            </Col>

            <Col md="4">
              <Label>{t('transfers.to')}: <span className="text-danger">*</span></Label>
              <WarehouseSelect
                allBranches
                value={form.destination_warehouse_id}
                onChange={(v) => update('destination_warehouse_id', v)}
              />
            </Col>

            <Col md="4">
              <Label>{t('common.date')}: <span className="text-danger">*</span></Label>
              <DatePicker
                selected={form.transfer_date}
                onChange={(d) => update('transfer_date', d)}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md="6">
              <Label>{t('transfers.driverName')}</Label>
              <Input value={form.driver_name} onChange={(e) => update('driver_name', e.target.value)} />
            </Col>
            <Col md="6">
              <Label>{t('transfers.vehicleRegistration')}</Label>
              <Input value={form.vehicle_registration} onChange={(e) => update('vehicle_registration', e.target.value)} />
            </Col>
          </Row>

          <Label className="mt-2">{t('transfers.items')}</Label>
          <Table bordered className="mb-2">
            <thead>
              <tr>
                <th style={{ width: '60%' }}>{t('inventory.product')}</th>
                <th style={{ width: '30%' }}>{t('transfers.quantity')}</th>
                <th style={{ width: '10%' }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={index}>
                  <td>
                    <CustomSelect
                      module="inventory/products"
                      value={line.product_id ? { value: line.product_id, label: line.product_name } : null}
                      onChange={(selected) => updateLine(index, {
                        product_id: selected ? selected.value : "",
                        product_name: selected ? selected.label : "",
                      })}
                      placeholder={t('inventory.searchProduct')}
                      canCreate={false}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      min="0"
                      step="0.001"
                      value={line.quantity_requested}
                      onChange={(e) => updateLine(index, { quantity_requested: e.target.value })}
                    />
                  </td>
                  <td className="text-center">
                    <IconButton
                      size="sm" outline color="danger"
                      disabled={lines.length === 1}
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <BsTrash3 />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <DefaultButton
            size="sm"
            outline
            color="secondary"
            onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
          >
            {t('transfers.addLine')}
          </DefaultButton>

          <FormGroup className="mt-3">
            <Label>{t('common.notes')}</Label>
            <Input
              type="textarea"
              rows="3"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
            />
          </FormGroup>

          <p className="text-muted small">{t('transfers.draftHint')}</p>
        </div>
      </div>

      <FixedSave onSave={submit} loading={saveLoading} disabled={saveLoading} />
    </SidebarLayout>
  );
}

export default CreateTransferPage;
